import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useToastStore } from '../../../stores/toastStore';
import {
  confirmClassification,
  type ClassificationResponse,
  type DocumentResultResponse,
} from '../api';
import { reviewerQueryKeys } from '../hooks/useDocumentResult';

interface ConfirmClassificationButtonProps {
  /** Job id required to call `POST /documents/{id}/confirm-classification`. */
  jobId: string;
  /** The job's auto-classification block; drives visibility. */
  classification: ClassificationResponse;
  /** Current document status; the button only makes sense while `review_required`. */
  status: string;
}

/**
 * «Confirmar tipo» — the missing half of the classification loop: while
 * ClassificationCorrectionControl reprocesses under a DIFFERENT type, this confirms the
 * auto-classified type as correct. The backend records the human decision, releases the
 * medium/low confidence cap and re-validates in the same request, so a document whose only
 * blocker was `classification_confidence:{band}` flips to `approved` on the spot — without
 * re-running OCR or the AI.
 *
 * Hidden for high-band or explicitly-typed documents (nothing to confirm), once confirmed,
 * and outside `review_required`.
 */
export function ConfirmClassificationButton({
  jobId,
  classification,
  status,
}: ConfirmClassificationButtonProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((state) => state.push);

  const mutation = useMutation({
    mutationFn: () => confirmClassification(jobId),
    onSuccess: (refreshed: DocumentResultResponse) => {
      // Same cache-replacement contract as ReValidateButton: banner + fields re-render without a refetch.
      queryClient.setQueryData(reviewerQueryKeys.result(jobId), refreshed);
      pushToast({
        tone: 'success',
        title: 'Tipo confirmado',
        description:
          refreshed.status === 'approved'
            ? 'Clasificación confirmada — el documento queda aprobado.'
            : 'Clasificación confirmada. Quedan otras validaciones pendientes.',
        durationMs: 3_500,
      });
    },
    onError: (cause: unknown) => {
      pushToast({
        tone: 'error',
        title: 'No se pudo confirmar el tipo',
        description: cause instanceof Error ? cause.message : 'Inténtalo de nuevo.',
      });
    },
  });

  const band = (classification.confidenceBand ?? '').toLowerCase();
  const visible =
    classification.wasAutoClassified &&
    classification.confirmed !== true &&
    (band === 'medium' || band === 'low') &&
    status === 'review_required';

  if (!visible) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
      data-testid="confirm-classification"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3.5 w-3.5">
        <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {mutation.isPending ? 'Confirmando…' : 'Confirmar tipo'}
    </button>
  );
}
